/* 3D 실행기. math3d 명령을 2D 실행기와 같은 계약(이름 등록부, 유효 개체 사후조건, 잔해 삭제)으로 부른다 */
import { acceptsText, entryOf } from './dsl';
import type { DslEntry, ToolCall, ToolResult } from './types';

export interface Math3DObject { id: number; name?: string; type?: string; category?: string; isValid?: boolean; position?: number[]; value?: number; relativeIntersectionObjects?: number[]; radius?: number; centerPointPosition?: number[]; bottomCenterPointPosition?: number[]; topCenterPointPosition?: number[] }
export interface Math3DApp {
  commandManager: { execute: (command: string, args?: unknown) => unknown };
  getStore: () => { getState: () => { object: { objectMap: Record<string, Math3DObject> }; common: { camera: { direction: number[]; up: number[] } } }; $camera?: { fov: number; aspect: number } };
}

/** 기본 평면 같은 시스템 개체는 id 가 안전 정수 끝자리라 사용자 개체와 가른다 */
const SYSTEM_ID_FROM = 9e15;
/** 결과가 비는 것이 정상인 명령. 평행하거나 꼬인 선은 엔진이 교점 그릇을 무효로 세우므로 종류가 맞는 입력이면 교점 0개로 본다 */
const EMPTY_IS_RESULT = ['object.addPointIntersection'];

const objectMap = (app: Math3DApp): Record<string, Math3DObject> => app.getStore().getState().object.objectMap;
export const allObjects3d = (app: Math3DApp): Math3DObject[] => Object.values(objectMap(app)).filter((o) => o.id < SYSTEM_ID_FROM);
const byId = (app: Math3DApp, id: number): Math3DObject | undefined => objectMap(app)[String(id)];
const settle = (): Promise<void> => new Promise((done) => { setTimeout(done, 0); });
const hint = (entry: DslEntry): string => { const text = acceptsText(entry); return text ? `. ${text}` : ''; };

/** 이름 에서 유효 개체 id 들로. 별칭(o1, o2 ...)은 실행마다 0부터 센다. 엔진의 개체 번호는 이어 세어져 실행마다 달라지므로 모델에게 보이지 않는다 */
export class Registry3D {
  private names = new Map<string, number[]>();
  private aliasById = new Map<number, string>();
  private idByAlias = new Map<string, number>();

  constructor(private app: Math3DApp) {}

  set(name: string, ids: number[]): void { this.names.set(name, ids); }

  aliasOf(id: number): string {
    let alias = this.aliasById.get(id);
    if (!alias) { alias = `o${this.aliasById.size + 1}`; this.aliasById.set(id, alias); this.idByAlias.set(alias, id); }
    return alias;
  }

  get(name: string): number[] {
    const aliased = this.idByAlias.get(name);
    if (aliased !== undefined && byId(this.app, aliased)) return [aliased];
    return (this.names.get(name) ?? []).filter((id) => byId(this.app, id));
  }

  knownNames(): string[] {
    return [...this.names.keys()].filter((n) => this.names.get(n)?.some((id) => byId(this.app, id)));
  }

  objectOf(name: string): Math3DObject | undefined {
    for (const id of this.names.get(name) ?? []) { const o = byId(this.app, id); if (o) return o; }
    return undefined;
  }
}

function parseArgs(call: ToolCall): Record<string, unknown> | null {
  try {
    const v = JSON.parse(call.function.arguments || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

/** 인자 이름 그대로 명령 인자 객체를 만든다. 개체를 가리키는 인자는 등록부로 풀어 엔진의 개체 번호로 바꾼다 */
function buildArgs(entry: DslEntry, args: Record<string, unknown>, registry: Registry3D): { values: Record<string, unknown>; missing: string[]; unknownRefs: string[] } {
  const values: Record<string, unknown> = {};
  const missing: string[] = [];
  const unknownRefs: string[] = [];
  const ref = (v: unknown): unknown => {
    if (typeof v !== 'string') return v;
    const ids = registry.get(v);
    if (!ids.length) { unknownRefs.push(v); return v; }
    return ids[0];
  };
  for (const p of entry.params) {
    const v = args[p.name];
    if (v === undefined || v === null || v === '') { if (!p.optional) missing.push(p.name); continue; }
    if (p.type === 'ref') values[p.name] = ref(v);
    else if (p.type.startsWith('ref[')) values[p.name] = Array.isArray(v) ? v.map(ref) : v;
    else if (p.type.startsWith('record:ref') && typeof v === 'object') values[p.name] = Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, one]) => [String(ref(k)), one]));
    else values[p.name] = v;
  }
  return { values, missing, unknownRefs };
}

/** 이번 호출이 만든 개체를 모두 지운다. 부모를 지우면 딸린 개체도 같이 지워지므로 남아 있는지 보고 지운다 */
function removeObjects(app: Math3DApp, ids: number[]): void {
  for (const id of ids) {
    if (!byId(app, id)) continue;
    try { app.commandManager.execute('object.deleteObject', { id }); } catch { /* 부모와 함께 지워진 개체 */ }
  }
}

/** 호출 하나를 실행하고 세운 유효 개체를 등록부에 올린다 */
export async function executeOne3d(app: Math3DApp, call: ToolCall, registry: Registry3D = new Registry3D(app)): Promise<ToolResult> {
  const name = call.function.name;
  const entry = entryOf(name);
  if (!entry || entry.tool !== 'math3d') return { call, ok: false, method: name, detail: `${name} 은 dsl.json 에 없는 math3d 명령이다` };
  const args = parseArgs(call);
  if (!args) return { call, ok: false, method: name, detail: `${name} 의 인자가 JSON 객체가 아니다` };
  const { values, missing, unknownRefs } = buildArgs(entry, args, registry);
  if (missing.length) return { call, ok: false, method: name, detail: `${name} 의 필수 인자가 비었다: ${missing.join(', ')}` };
  if (unknownRefs.length) return { call, ok: false, method: name, detail: `${name} 이 가리킨 ${unknownRefs.map((r) => `"${r}"`).join(', ')} 은 세워진 개체의 이름이 아니다` };
  const method = `commandManager.execute(${entry.command ?? name})`;
  const before = new Set(allObjects3d(app).map((o) => o.id));
  const newIds = (): number[] => allObjects3d(app).filter((o) => !before.has(o.id)).map((o) => o.id);
  let response: unknown;
  try {
    response = app.commandManager.execute(entry.command ?? name, values);
  } catch (error) {
    removeObjects(app, newIds());
    return { call, ok: false, method, detail: `${method} 예외: ${error instanceof Error ? error.message : String(error)}${hint(entry)}` };
  }
  await settle();
  // 속성을 바꾸는 명령(점선 같은)은 개체를 만들지 않는다. 예외 없이 끝나고 새 개체가 없으면 성공이다
  if (!entry.name.startsWith('object.add')) {
    const stray = newIds();
    if (stray.length) { removeObjects(app, stray); return { call, ok: false, method, detail: `${method} 는 개체를 만드는 명령이 아닌데 개체가 생겼다. 잔해는 지웠다` }; }
    return { call, ok: true, method, detail: JSON.stringify({ set: true }), response: { set: true } };
  }
  // 접선처럼 번호 배열을 돌려주는 명령이 있다. 첫 번호가 주 개체이고 나머지는 같은 호출의 짝이다
  const returned = (Array.isArray(response) ? response : [response]).filter((id): id is number => typeof id === 'number');
  const primary = returned.length ? byId(app, returned[0]) : undefined;
  if (!primary) {
    removeObjects(app, newIds());
    return { call, ok: false, method, detail: `${method} 가 유효한 개체의 번호를 돌려주지 않았다. 잔해는 지웠다${hint(entry)}` };
  }
  // 교점은 그릇 개체와 보조점으로 선다. 이후 호출이 가리키는 것은 보조점이다
  const standing: Math3DObject[] = primary.type === 'intersection'
    ? (primary.relativeIntersectionObjects ?? []).map((id) => byId(app, id)).filter((o): o is Math3DObject => !!o && o.isValid !== false)
    : returned.map((id) => byId(app, id)).filter((o): o is Math3DObject => !!o && o.isValid !== false);
  const requested = typeof args.name === 'string' && args.name ? args.name : '';
  const bad = primary.type === 'intersection' ? primary.isValid === false : standing.length < returned.length;
  if (!standing.length || bad) {
    removeObjects(app, [...returned, ...newIds()]);
    if (EMPTY_IS_RESULT.includes(name)) { if (requested) registry.set(requested, []); return { call, ok: true, method, detail: JSON.stringify({ id: registry.aliasOf(primary.id), name: requested || registry.aliasOf(primary.id), standing: 0 }), response: { standing: 0 } }; }
    return { call, ok: false, method, detail: `"${requested || name}" 이 유효하게 서지 않았다(인자의 종류나 배치가 맞지 않다). 잔해는 지웠다${hint(entry)}`, response: { id: primary.id, standing: 0 } };
  }
  const sceneName = requested || registry.aliasOf(standing[0].id);
  standing.forEach((o, i) => { if (requested) registry.set(i === 0 ? sceneName : `${sceneName}_${i + 1}`, [o.id]); else registry.aliasOf(o.id); });
  const first = standing[0];
  return {
    call, ok: true, method,
    detail: JSON.stringify({ id: registry.aliasOf(first.id), name: sceneName, standing: standing.length, ...(first.type === 'measurement' ? { value: first.value } : {}) }),
    response: { id: first.id, name: sceneName, standing: standing.length, value: first.value, position: first.position },
  };
}

const registries = new WeakMap<Math3DApp, Registry3D>();

/** 앱마다 하나의 등록부. 단계가 달라도 앞 단계가 만든 개체를 요청한 이름으로 가리킬 수 있다 */
export function registryOf3d(app: Math3DApp): Registry3D {
  let registry = registries.get(app);
  if (!registry) { registry = new Registry3D(app); registries.set(app, registry); }
  return registry;
}

/** 화면을 비울 때 등록부도 비운다 */
export function resetRegistry3d(app: Math3DApp): void {
  registries.delete(app);
}

/** 호출 묶음을 순서대로 실행한다. 앞 호출이 세운 이름을 뒤 호출이 가리킨다 */
export async function executeToolCalls3d(app: Math3DApp, calls: ToolCall[], afterEach?: (names: string[]) => Promise<void>): Promise<ToolResult[]> {
  const registry = registryOf3d(app);
  const out: ToolResult[] = [];
  for (const call of calls) {
    out.push(await executeOne3d(app, call, registry));
    if (afterEach) await afterEach(registry.knownNames());
  }
  return out;
}
